import { useTranslation } from "@opendash/core";
import { useNavigate } from "@opendash/router";
import { Button, Card, Divider, Flex, Typography } from "antd";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTicketManager } from "../..";
import { Project } from "../../../../js/parsegenerated";
import { OverviewChart } from "../../_shared/components/statistics/OverviewChart";
import { useProject } from "../hooks/useProject";
import { useProjects } from "../hooks/useProjects";
import { ProjectList } from "./card-items/ProjectList";
import { ProjectTitle } from "./card-items/ProjectTitle";
import { ProjectAssignment } from "./ProjectAssignment";

type Props = {
  parent?: Project;
  hideSubProjects?: boolean;
};

/**
 * Recursive view of a project with all sub projects that can be navigated to and back from.
 */
export const ProjectCard = observer((props: Props) => {
  const nav = useNavigate();
  const t = useTranslation();
  const { project, color, colorText } = useProject();
  const { projects: subProjects } = useProjects(project, true);

  const ticketOverviewManager = useTicketManager(
    {
      ticketsPerPage: 1000,
      filters: {
        project: {
          attribute: "project",
          value: project.id,
        },
      },
    },
    true
  );

  return (
    <Card
      variant="outlined"
      title={
        <ProjectTitle
          parent={props.parent}
          styles={{
            text: {
              color: colorText,
              backgroundColor: color,
              width: "100%",
              height: "100%",
            },
            icon: {
              color: colorText,
            },
            duedate: {
              color: colorText,
            },
            back: {
              color: colorText,
            },
          }}
        />
      }
      color={project.get("color")}
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
      styles={{
        header: { backgroundColor: color },
        body: {
          flexGrow: 1,
        },
      }}
    >
      <Flex vertical justify="space-between" style={{ height: "100%" }}>
        <Flex vertical gap="small" style={{ marginBottom: "1rem" }}>
          <div /> {/* spacer for button bottom */}
          {project.get("description") && (
            <Typography.Text>{project.get("description")}</Typography.Text>
          )}
          {ticketOverviewManager.totalCount > 0 && (
            <div>
              <Divider children={t("maintenance:ticket.project.statistics")} />
              <OverviewChart ticketManager={ticketOverviewManager} />
            </div>
          )}
          <div>
            <Divider children={t("maintenance:project.assignedUserAndRoles")} />
            <ProjectAssignment />
          </div>
          {subProjects.length > 0 && !props.hideSubProjects && (
            <div>
              <Divider children={t("maintenance:ticket.project.subprojects")} />
              <Flex vertical gap="small">
                <ProjectList projects={subProjects} />
              </Flex>
            </div>
          )}
        </Flex>

        <Divider
          children={
            <Button
              onClick={() => nav(`/openservice/project/${project.id}`)}
              style={{
                backgroundColor: color,
                color: colorText,
              }}
            >
              {t("maintenance:project.navigate.single")}
            </Button>
          }
        />
      </Flex>
    </Card>
  );
});
